// Unified Error Handler Service for OMNIGATE
import { SystemLog } from '../types';

export type ErrorLevel = 'ERROR' | 'WARN' | 'INFO' | 'DEBUG' | 'AI_ACTION';
export type ErrorSource = 'AGENT' | 'NETWORK' | 'SYSTEM' | 'MCP' | 'UI' | 'KERNEL';

export interface OmniaGateError {
  id: string;
  timestamp: string;
  level: ErrorLevel;
  message: string;
  source: ErrorSource;
  details?: string;
  stack?: string;
  component?: string;
  errorCode?: string;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private subscribers: Array<(error: OmniaGateError) => void> = [];
  private errorHistory: OmniaGateError[] = [];
  private globalHandlersInstalled = false;

  private constructor() {}

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  // Subscribe to error events
  public subscribe(callback: (error: OmniaGateError) => void): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }

  // Capture and process errors
  public captureError(
    error: Error | string,
    level: ErrorLevel = 'ERROR',
    source: ErrorSource = 'SYSTEM',
    component?: string,
    details?: string
  ): OmniaGateError {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'object' ? (error as Error).stack : undefined;
    const errorCode = typeof error === 'object' ? (error as any).code : undefined;

    const omniError: OmniaGateError = {
      id: `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 5).toUpperCase()}`,
      timestamp: new Date().toLocaleTimeString(),
      level,
      message: errorMessage,
      source,
      details,
      stack,
      component,
      errorCode
    };

    // Store in history (keep last 100 errors)
    this.errorHistory.unshift(omniError);
    if (this.errorHistory.length > 100) {
      this.errorHistory = this.errorHistory.slice(0, 100);
    }

    // Notify all subscribers
    this.subscribers.forEach(callback => {
      try {
        callback(omniError);
      } catch (e) {
        console.error('Error in error subscriber:', e);
      }
    });

    // Also log to console for debugging
    this.logToConsole(omniError);

    return omniError;
  }

  // Convenience methods for different error types
  public agentError(message: string, details?: string, component?: string): OmniaGateError {
    return this.captureError(message, 'ERROR', 'AGENT', component, details);
  }

  public networkError(message: string, details?: string, component?: string): OmniaGateError {
    return this.captureError(message, 'ERROR', 'NETWORK', component, details);
  }

  public systemError(message: string, details?: string, component?: string): OmniaGateError {
    return this.captureError(message, 'ERROR', 'SYSTEM', component, details);
  }

  public mcpError(message: string, details?: string, component?: string): OmniaGateError {
    return this.captureError(message, 'ERROR', 'MCP', component, details);
  }

  public warning(message: string, source: ErrorSource = 'SYSTEM', component?: string): OmniaGateError {
    return this.captureError(message, 'WARN', source, component);
  }

  public info(message: string, source: ErrorSource = 'SYSTEM', component?: string): OmniaGateError {
    return this.captureError(message, 'INFO', source, component);
  }

  // Convert to SystemLog format for compatibility
  public toSystemLog(error: OmniaGateError): SystemLog {
    return {
      id: error.id,
      timestamp: error.timestamp,
      level: error.level,
      message: error.message,
      details: error.details,
      component: error.component
    };
  }

  // Get error history
  public getErrorHistory(): OmniaGateError[] {
    return [...this.errorHistory];
  }

  // Clear error history
  public clearHistory(): void {
    this.errorHistory = [];
  }

  // Log to console with appropriate styling
  private logToConsole(error: OmniaGateError): void {
    const logStyles = {
      ERROR: 'color: #ef4444; font-weight: bold;',
      WARN: 'color: #f59e0b; font-weight: bold;',
      INFO: 'color: #3b82f6; font-weight: bold;',
      DEBUG: 'color: #8b5cf6; font-weight: bold;',
      AI_ACTION: 'color: #10b981; font-weight: bold;'
    };

    const style = logStyles[error.level] || logStyles.INFO;
    const payload = {
      id: error.id,
      message: error.message,
      component: error.component,
      details: error.details,
      stack: error.stack,
      errorCode: error.errorCode,
    };

    if (error.level === 'ERROR') {
      console.error(`%c[${error.timestamp}] ${error.level} [${error.source}] ${error.message}`, style, payload);
      return;
    }

    console.log(`%c[${error.timestamp}] ${error.level} [${error.source}] ${error.message}`, style, payload);
  }

  // Global error handler setup
  public setupGlobalHandlers(): void {
    if (this.globalHandlersInstalled) return;
    this.globalHandlersInstalled = true;

    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      const error = event.error instanceof Error
        ? event.error
        : new Error(event.message || 'Uncaught browser error');
      this.captureError(error, 'ERROR', 'SYSTEM', 'Global', `${event.filename}:${event.lineno}:${event.colno}`);
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(
        event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        'ERROR',
        'SYSTEM',
        'Global',
        'Unhandled promise rejection'
      );
    });
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Hook for React components to subscribe to errors
export const useErrorHandler = () => {
  return errorHandler;
};
